"use client";

import React from "react";
import { FaqWithWatchVideo } from "@bfl-ui-toolkit/faq-with-watch-video";
import { IFaqs } from "../../interfaces/IAMCContent.interface";
import { currencify } from "@repo/shared-utils/num-utils";
import { IAmcDetails, IInfo } from "../../interfaces/AMCChartData.interface";
import { dataLayer, CombinedClevertapProps } from "@repo/analytics/data-layer-utils";
import { selectLoginStatus, useAppSelector } from "@repo/global-store";

interface FaqsDataProps {
  faqsData: IFaqs;
  amcDetails: IAmcDetails;
}

type IFaqItem = {
  question?: string;
  answer?: string;
  watchvideogccId?: string;
};

type IFaqGroup = Record<string, IFaqItem>;

export const AMCFaqs: React.FC<FaqsDataProps> = ({ faqsData, amcDetails }) => {
  const {
    faqData,
    componentPosition,
    mobileFaqCount,
    showless,
    showmore,
    desktopFaqCount,
    faqVariation,
    hideInDesktop,
    hideInMobile,
    showOnApp,
    showOnWeb,
    enableIpp,
    title,
  } = faqsData ?? {};

  const loginStatus = useAppSelector(selectLoginStatus);

  const replaceDynamicValues = (text: string) => {
    if (!text) return "";

    const info: Partial<IInfo> = amcDetails?.info ?? {};

    const { nav = "", navDate = "", ratios: { expRatio = "" } = {} } = info ?? {};

    const map: Record<string, string> = {
      nav: nav ? currencify(nav) : "",
      navDate: navDate ? new Date(navDate).toLocaleDateString("en-IN") : "",
      expRatio: expRatio ? String(expRatio) : "",
    };

    return text.replaceAll(/\{([^{}]+)\}/g, (_, key: string): string => {
      const typedKey = key as keyof IInfo;
      const infoValue = info[typedKey];
      return (
        map[key] ?? (infoValue !== undefined && infoValue !== null ? String(infoValue) : `{${key}}`)
      );
    });
  };

  const accordianDetails = faqData?.map((group: IFaqGroup) => {
    const formattedItems: Record<
      string,
      {
        accordianTitle: string;
        accordianDescription: string;
        accordionVideoCTAType: string;
        openInNewTab: string;
        videoId: string;
      }
    > = {};

    Object.values(group ?? {}).forEach((item: IFaqItem, index) => {
      const videoId = /gcc-[0-9a-fA-F-]+(?=\.)/.exec(item?.watchvideogccId ?? "")?.[0] ?? "";

      formattedItems[`item${index + 1}`] = {
        accordianTitle: replaceDynamicValues(item.question ?? ""),
        accordianDescription: replaceDynamicValues(item.answer ?? ""),
        accordionVideoCTAType: "link",
        openInNewTab: "false",
        videoId,
      };
    });

    return formattedItems;
  });

  const trackFaqCardClickEvent = () => {
    const payload = {
      component: "amc_faq",
      element: "faq_card_click",
      event: "click",
      event_type: "content_cards_faqs_list_icon_click",
      destination_url: "NA",
      section_title: title,
      cta_text: "FAQ Card",
      product_name: "bajaj_mutual_fund",
      category_name: "mutual_fund",
      page_type: "pdp",
      component_position: componentPosition,
      component_id: "amc_faq",
    };

    dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  const trackFaqVideoClickEvent = () => {
    const payload = {
      component: "amc_faq",
      element: "faq_video_click",
      event: "click",
      event_type: "content_cards_faqs_list_video_click",
      destination_url: "NA",
      section_title: title,
      cta_text: "FAQ Video",
      product_name: "bajaj_mutual_fund",
      category_name: "mutual_fund",
      page_type: "pdp",
      component_position: componentPosition,
      component_id: "amc_faq",
    };

    dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  const trackEvent = (payload?: { clickType?: string }) => {
    switch (payload?.clickType) {
      case "content_cards_faqs_list_icon_click":
        trackFaqCardClickEvent();
        break;

      case "content_cards_faqs_list_video_click":
        trackFaqVideoClickEvent();
        break;

      default:
        break;
    }
  };

  const allFaqs = faqData?.flatMap((group) => Object.values(group ?? {})) ?? [];

  let faqSchema = {};
  if (faqData?.length) {
    faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqs.map((faq) => ({
        "@type": "Question",
        name: replaceDynamicValues(faq?.question ?? ""),
        acceptedAnswer: {
          "@type": "Answer",
          text: replaceDynamicValues(faq?.answer ?? ""),
        },
      })),
    };
  }

  return (
    <>
      <div id="faqs" className="mx-[-16px] my-[-12px] md:m-0">
        <FaqWithWatchVideo
          content={{
            accordianDetails,
            componentLink: "faq-with-video",
            componentPostion: componentPosition,
            componentTitle: "FAQ",
            ctaType: "default",
            desktopFaqCount,
            enableIpp,
            faqTitle: title,
            faqVariation,
            hideInDesktop,
            hideInMobile,
            mobileFaqCount,
            showLess: showless,
            showMore: showmore,
            showOnApp,
            showOnWeb,
          }}
          trackEvent={trackEvent}
        />
      </div>
      {faqData?.length ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      ) : null}
    </>
  );
};

export default AMCFaqs;
